import { WebDashboardContentSpec } from "@coeff/agent-types";
import { WebDashboardBuildingPhase } from "@coeff/api";
import React, { useEffect, useMemo, useState } from "react";

import { CoeLogo } from "../../components";

type LoaderPhase =
  | typeof WebDashboardBuildingPhase.Started
  | typeof WebDashboardBuildingPhase.ExploringData
  | typeof WebDashboardBuildingPhase.PlanningLayout
  | typeof WebDashboardBuildingPhase.BuildingDashboard
  | typeof WebDashboardBuildingPhase.Completed
  | typeof WebDashboardBuildingPhase.Failed;

// Completed (build done) and Failed (build crashed) are terminal — neither is a "building
// progress" step the loader animates, so both are excluded. The viewer renders the dashboard for
// Completed and a failure state for Failed; the loader is never shown for either.
type VisibleLoaderPhase = Exclude<
  LoaderPhase,
  typeof WebDashboardBuildingPhase.Completed | typeof WebDashboardBuildingPhase.Failed
>;

interface DashboardBuildLoaderProps {
  phase?: LoaderPhase | null;
  contentSpec?: WebDashboardContentSpec;
}

interface DashboardBuildProgressState {
  resolvedPhase: VisibleLoaderPhase;
  activeProgress: number;
}

const PHASE_LABELS: Record<VisibleLoaderPhase, string> = {
  [WebDashboardBuildingPhase.Started]: "Starting",
  [WebDashboardBuildingPhase.ExploringData]: "Exploring",
  [WebDashboardBuildingPhase.PlanningLayout]: "Planning",
  [WebDashboardBuildingPhase.BuildingDashboard]: "Building",
};

const PHASE_DESCRIPTIONS: Record<VisibleLoaderPhase, string> = {
  [WebDashboardBuildingPhase.Started]:
    "Warming up the builder and preparing to read your spreadsheet.",
  [WebDashboardBuildingPhase.ExploringData]:
    "Reading your sheet, scanning columns, and finding useful patterns.",
  [WebDashboardBuildingPhase.PlanningLayout]:
    "Designing the dashboard structure and choosing the right blocks.",
  [WebDashboardBuildingPhase.BuildingDashboard]:
    "Generating blocks in parallel. Finished blocks are saved as soon as they are ready.",
};

const CONTENT_PHASE_STEPS = [
  { id: "analyzing", label: "Analyzing" },
  { id: "planning", label: "Planning" },
  { id: "building", label: "Building" },
] as const;

const CONTENT_PHASE_COPY: Record<VisibleLoaderPhase, { title: string; subtitle: string }> = {
  [WebDashboardBuildingPhase.Started]: {
    title: "Analyzing spreadsheet data...",
    subtitle: "This may take a few minutes",
  },
  [WebDashboardBuildingPhase.ExploringData]: {
    title: "Analyzing spreadsheet data...",
    subtitle: "This may take a few minutes",
  },
  [WebDashboardBuildingPhase.PlanningLayout]: {
    title: "Planning your dashboard",
    subtitle: "This may take up to a minute",
  },
  [WebDashboardBuildingPhase.BuildingDashboard]: {
    title: "Planning your dashboard",
    subtitle: "This may take up to a minute",
  },
};

const SKEL_COLS = 6;
const SHEET_SCAN_INTERVAL_MS = 1000;
const PLANNING_SHUFFLE_GAP = 10;
const PLANNING_SHUFFLE_WIDTH = 400;
const PLANNING_SHUFFLE_HEIGHT = 320;
const PLANNING_SHUFFLE_COL_WIDTH = (PLANNING_SHUFFLE_WIDTH - PLANNING_SHUFFLE_GAP * 3) / 4;
const PLANNING_SHUFFLE_ROW_1_HEIGHT = 70;
const PLANNING_SHUFFLE_ROW_2_HEIGHT = 90;
const PLANNING_SHUFFLE_ROW_3_HEIGHT =
  PLANNING_SHUFFLE_HEIGHT -
  PLANNING_SHUFFLE_ROW_1_HEIGHT -
  PLANNING_SHUFFLE_ROW_2_HEIGHT -
  PLANNING_SHUFFLE_GAP * 2;

type PlanningShuffleRect = {
  x: number;
  y: number;
  w: number;
  h: number;
};

type PlanningShuffleStep = {
  layout: PlanningShuffleRect[];
  hold: number;
  lift?: boolean;
  settled?: boolean;
};

const PLANNING_SHUFFLE_FINAL: PlanningShuffleRect[] = [
  { x: 0, y: 0, w: PLANNING_SHUFFLE_COL_WIDTH, h: PLANNING_SHUFFLE_ROW_1_HEIGHT },
  {
    x: PLANNING_SHUFFLE_COL_WIDTH + PLANNING_SHUFFLE_GAP,
    y: 0,
    w: PLANNING_SHUFFLE_COL_WIDTH,
    h: PLANNING_SHUFFLE_ROW_1_HEIGHT,
  },
  {
    x: (PLANNING_SHUFFLE_COL_WIDTH + PLANNING_SHUFFLE_GAP) * 2,
    y: 0,
    w: PLANNING_SHUFFLE_COL_WIDTH,
    h: PLANNING_SHUFFLE_ROW_1_HEIGHT,
  },
  {
    x: (PLANNING_SHUFFLE_COL_WIDTH + PLANNING_SHUFFLE_GAP) * 3,
    y: 0,
    w: PLANNING_SHUFFLE_COL_WIDTH,
    h: PLANNING_SHUFFLE_ROW_1_HEIGHT,
  },
  {
    x: 0,
    y: PLANNING_SHUFFLE_ROW_1_HEIGHT + PLANNING_SHUFFLE_GAP,
    w: (PLANNING_SHUFFLE_WIDTH - PLANNING_SHUFFLE_GAP) / 2,
    h: PLANNING_SHUFFLE_ROW_2_HEIGHT,
  },
  {
    x: (PLANNING_SHUFFLE_WIDTH - PLANNING_SHUFFLE_GAP) / 2 + PLANNING_SHUFFLE_GAP,
    y: PLANNING_SHUFFLE_ROW_1_HEIGHT + PLANNING_SHUFFLE_GAP,
    w: (PLANNING_SHUFFLE_WIDTH - PLANNING_SHUFFLE_GAP) / 2,
    h: PLANNING_SHUFFLE_ROW_2_HEIGHT,
  },
  {
    x: 0,
    y: PLANNING_SHUFFLE_ROW_1_HEIGHT + PLANNING_SHUFFLE_ROW_2_HEIGHT + PLANNING_SHUFFLE_GAP * 2,
    w: PLANNING_SHUFFLE_WIDTH,
    h: PLANNING_SHUFFLE_ROW_3_HEIGHT,
  },
];

const PLANNING_SHUFFLE_INITIAL: PlanningShuffleRect[] = Array.from({ length: 7 }, (_, index) => {
  const col = index % 4;
  const row = Math.floor(index / 4);

  return {
    x: 20 + col * 90 + Math.sin(index) * 10,
    y: 30 + row * 100 + Math.cos(index) * 8,
    w: 80,
    h: 60,
  };
});

const PLANNING_SHUFFLE_DRAFTS: PlanningShuffleRect[][] = [
  [
    { x: 0, y: 0, w: 120, h: 90 },
    { x: 130, y: 0, w: 120, h: 90 },
    { x: 260, y: 0, w: 140, h: 90 },
    { x: 0, y: 100, w: 95, h: 90 },
    { x: 105, y: 100, w: 95, h: 90 },
    { x: 210, y: 100, w: 95, h: 90 },
    { x: 0, y: 200, w: 400, h: 120 },
  ],
  [
    { x: 0, y: 0, w: 195, h: 110 },
    { x: 205, y: 0, w: 195, h: 110 },
    { x: 0, y: 120, w: 195, h: 90 },
    { x: 205, y: 120, w: 195, h: 90 },
    { x: 0, y: 220, w: 130, h: 100 },
    { x: 140, y: 220, w: 130, h: 100 },
    { x: 280, y: 220, w: 120, h: 100 },
  ],
  [
    { x: 0, y: 0, w: 180, h: 200 },
    { x: 190, y: 0, w: 100, h: 95 },
    { x: 300, y: 0, w: 100, h: 95 },
    { x: 190, y: 105, w: 210, h: 95 },
    { x: 0, y: 210, w: 195, h: 110 },
    { x: 205, y: 210, w: 95, h: 110 },
    { x: 310, y: 210, w: 90, h: 110 },
  ],
];

const PLANNING_SHUFFLE_SEQUENCE: PlanningShuffleStep[] = [
  { layout: PLANNING_SHUFFLE_INITIAL, hold: 700 },
  { layout: PLANNING_SHUFFLE_DRAFTS[0], hold: 1300, lift: true },
  { layout: PLANNING_SHUFFLE_DRAFTS[1], hold: 1300, lift: true },
  { layout: PLANNING_SHUFFLE_DRAFTS[2], hold: 1300, lift: true },
  { layout: PLANNING_SHUFFLE_FINAL, hold: 2000, settled: true },
];

const getVisiblePhase = (phase?: LoaderPhase | null): VisibleLoaderPhase => {
  // No phase, or a terminal Failed (loader shouldn't be shown for Failed, but fall back safely
  // rather than indexing a missing map key if it ever is).
  if (!phase || phase === WebDashboardBuildingPhase.Failed) {
    return WebDashboardBuildingPhase.Started;
  }
  if (
    phase === WebDashboardBuildingPhase.Completed ||
    phase === WebDashboardBuildingPhase.BuildingDashboard
  ) {
    return WebDashboardBuildingPhase.PlanningLayout;
  }
  return phase;
};

const getContentPhaseIndex = (phase: VisibleLoaderPhase): number => {
  if (
    phase === WebDashboardBuildingPhase.Started ||
    phase === WebDashboardBuildingPhase.ExploringData
  ) {
    return 0;
  }
  if (phase === WebDashboardBuildingPhase.PlanningLayout) {
    return 1;
  }
  return 2;
};

const useActivePhaseProgress = (phase: VisibleLoaderPhase): number => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const intervalId = window.setInterval(() => {
      setTick(Date.now() - start);
    }, 120);

    return () => window.clearInterval(intervalId);
  }, [phase]);

  // Keep the active step moving while backend polling determines the real transition.
  return Math.min(0.92, 0.08 + (tick / 18000) * 0.84);
};

const useDashboardBuildProgressState = (
  phase?: LoaderPhase | null
): DashboardBuildProgressState => {
  const resolvedPhase = getVisiblePhase(phase);
  const activeProgress = useActivePhaseProgress(resolvedPhase);

  return {
    resolvedPhase,
    activeProgress,
  };
};

export const getDashboardBuildPhaseLabel = (phase?: LoaderPhase | null): string =>
  PHASE_LABELS[getVisiblePhase(phase)];

export const getDashboardBuildPhaseDescription = (phase?: LoaderPhase | null): string =>
  PHASE_DESCRIPTIONS[getVisiblePhase(phase)];

export const DashboardBuildLoaderStyles: React.FC = () => <style>{LOADER_STYLES}</style>;

const Splash: React.FC<{ active: boolean }> = ({ active }) => {
  return (
    <div
      className="splash"
      style={{
        opacity: active ? 1 : 0,
        pointerEvents: active ? "auto" : "none",
      }}
    >
      <div className="splash-mark" />
    </div>
  );
};

const SpreadsheetLoadingIllustration: React.FC = () => {
  const [scanCol, setScanCol] = useState(0);
  const cellWidths = useMemo(() => {
    return Array.from({ length: 10 }, (_, rowIndex) =>
      Array.from({ length: SKEL_COLS }, (_, columnIndex) => {
        const seed = (rowIndex * 7 + columnIndex * 13) % 100;
        return 30 + (seed % 42);
      })
    );
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setScanCol(currentScanCol => (currentScanCol + 1) % SKEL_COLS);
    }, SHEET_SCAN_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <div className="content-loader-sheet" aria-hidden>
      <div className="content-loader-sheet-inner">
        <div className="content-loader-row content-loader-head">
          {Array.from({ length: SKEL_COLS }).map((_, columnIndex) => (
            <div
              className={`content-loader-cell ${columnIndex === scanCol ? "active" : ""}`}
              key={columnIndex}
            >
              <div className="content-loader-bar head" />
            </div>
          ))}
        </div>
        {cellWidths.map((row, rowIndex) => (
          <div className="content-loader-row" key={rowIndex}>
            {row.map((width, columnIndex) => (
              <div
                className={`content-loader-cell ${columnIndex === scanCol ? "active" : ""}`}
                key={columnIndex}
              >
                <div className="content-loader-bar" style={{ width: `${width}%` }} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const PlanningShuffleIllustration: React.FC = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const step = PLANNING_SHUFFLE_SEQUENCE[stepIndex % PLANNING_SHUFFLE_SEQUENCE.length];

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setStepIndex(currentStepIndex => currentStepIndex + 1);
    }, step.hold);

    return () => window.clearTimeout(timeoutId);
  }, [step.hold, stepIndex]);

  return (
    <div className="planning-shuffle-illustration" aria-hidden>
      <div className="planning-shuffle-grid">
        {PLANNING_SHUFFLE_FINAL.map((slot, slotIndex) => (
          <div
            className={`planning-shuffle-slot ${step.settled ? "settled" : ""}`}
            key={`slot-${slotIndex}`}
            style={{
              left: slot.x,
              top: slot.y,
              width: slot.w,
              height: slot.h,
            }}
          />
        ))}
        {step.layout.map((card, cardIndex) => (
          <div
            className={`planning-shuffle-card ${step.lift ? "lifted" : ""}`}
            key={`card-${cardIndex}`}
            style={{
              left: card.x,
              top: card.y,
              width: card.w,
              height: card.h,
            }}
          />
        ))}
      </div>
    </div>
  );
};

const ContentPhaseProgress: React.FC<{ phase: VisibleLoaderPhase; activeProgress: number }> = ({
  phase,
  activeProgress,
}) => {
  const activeIndex = getContentPhaseIndex(phase);

  return (
    <div className="content-loader-progress" aria-label="Dashboard build progress">
      {CONTENT_PHASE_STEPS.map((step, index) => {
        const width =
          index < activeIndex
            ? 100
            : index === activeIndex
            ? Math.max(8, Math.round(activeProgress * 100))
            : 0;
        const label = step.id === "analyzing" && activeIndex > 0 ? "Analyzed" : step.label;
        return (
          <div className="content-loader-progress-step" key={step.id}>
            <div className="content-loader-progress-track">
              <div className="content-loader-progress-fill" style={{ width: `${width}%` }} />
            </div>
            <div className={index === activeIndex ? "active" : ""}>{label}</div>
          </div>
        );
      })}
    </div>
  );
};

export const DashboardBuildPhaseProgress: React.FC<{
  phase?: LoaderPhase | null;
}> = ({ phase }) => {
  const progressPhase =
    !phase ||
    phase === WebDashboardBuildingPhase.Completed ||
    phase === WebDashboardBuildingPhase.Failed
      ? WebDashboardBuildingPhase.Started
      : phase;
  const activeProgress = useActivePhaseProgress(progressPhase);

  return <ContentPhaseProgress phase={progressPhase} activeProgress={activeProgress} />;
};

const ContentLoadingPhase: React.FC<{
  phase: VisibleLoaderPhase;
}> = ({ phase }) => {
  const copy = CONTENT_PHASE_COPY[phase];
  const isPlanningPhase =
    phase === WebDashboardBuildingPhase.PlanningLayout ||
    phase === WebDashboardBuildingPhase.BuildingDashboard;

  return (
    <div className="phase active content-loader-phase">
      <div className="content-loader-center">
        {isPlanningPhase ? <PlanningShuffleIllustration /> : <SpreadsheetLoadingIllustration />}
        <div className="content-loader-copy">
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </div>
      </div>
    </div>
  );
};

const StartedPhase: React.FC<{ active: boolean }> = ({ active }) => {
  if (!active) {
    return null;
  }
  return <ContentLoadingPhase phase={WebDashboardBuildingPhase.Started} />;
};

const ExplorePhase: React.FC<{ active: boolean }> = ({ active }) => {
  if (!active) {
    return null;
  }
  return <ContentLoadingPhase phase={WebDashboardBuildingPhase.ExploringData} />;
};

const PlanPhase: React.FC<{
  active: boolean;
}> = ({ active }) => {
  if (!active) {
    return null;
  }
  return <ContentLoadingPhase phase={WebDashboardBuildingPhase.PlanningLayout} />;
};

export const DashboardBuildContentLoader: React.FC<{
  phase?: LoaderPhase | null;
  contentSpec?: WebDashboardContentSpec;
  className?: string;
}> = ({ phase, className = "" }) => {
  const { resolvedPhase } = useDashboardBuildProgressState(phase);

  return (
    <div className={`canvas build-loader-canvas ${className}`}>
      <>
        <Splash active={false} />
        <StartedPhase active={resolvedPhase === WebDashboardBuildingPhase.Started} />
        <ExplorePhase active={resolvedPhase === WebDashboardBuildingPhase.ExploringData} />
        <PlanPhase active={resolvedPhase === WebDashboardBuildingPhase.PlanningLayout} />
      </>
    </div>
  );
};

/** The loader's page surface. The viewer paints the whole page (incl. behind the transparent
    nav) in this color while the loader is up, so the build screen reads as one flat surface. */
export const BUILD_LOADER_PAGE_BACKGROUND = "#f7f7f8";

const LOADER_STYLES = `
.dashboard-build-loader {
  --bg: #fafbfc;
  --bg-2: #f3f5f8;
  --bg-3: #eaeef3;
  --ink: #0a0e1a;
  --ink-2: #3a4255;
  --ink-3: #6b7487;
  --ink-4: #9aa3b4;
  --line: #e4e8ef;
  --line-2: #d6dce6;
  --accent: #3a5cff;
  --accent-2: #1db6b0;
  --accent-3: #8a5cf6;
  --pos: #0d9c6a;
  --warn: #e06b2b;
  --ease: cubic-bezier(0.22, 1, 0.36, 1);
  min-height: 100vh;
  width: 100%;
  color: var(--ink);
  background: ${BUILD_LOADER_PAGE_BACKGROUND};
  font-family: Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  overflow: hidden;
}
.dashboard-build-embedded {
  --bg: #fafbfc;
  --bg-2: #f3f5f8;
  --bg-3: #eaeef3;
  --ink: #0a0e1a;
  --ink-2: #3a4255;
  --ink-3: #6b7487;
  --ink-4: #9aa3b4;
  --line: #e4e8ef;
  --line-2: #d6dce6;
  --accent: #3a5cff;
  --accent-2: #1db6b0;
  --accent-3: #8a5cf6;
  --pos: #0d9c6a;
  --warn: #e06b2b;
  --ease: cubic-bezier(0.22, 1, 0.36, 1);
  color: var(--ink);
}
.dashboard-build-loader * { box-sizing: border-box; }
.dashboard-build-embedded * { box-sizing: border-box; }
.build-loader-navbar {
  position: sticky;
  top: 0;
  z-index: 200;
  height: 64px;
  display: flex;
  align-items: center;
  padding: 12px 24px;
  border-bottom: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.88);
  color: #111827;
  backdrop-filter: blur(8px);
}
.build-loader-nav-inner {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.build-loader-nav-left {
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 20px;
}
.build-loader-logo {
  width: 112px;
  min-width: 80px;
}
.build-loader-nav-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.build-loader-nav-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.build-loader-nav-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #111827;
  padding: 8px 12px;
  font: inherit;
  font-size: 13px;
  transition: background 160ms var(--ease), color 160ms var(--ease);
}
.build-loader-nav-button:disabled {
  cursor: default;
  opacity: 1;
}
.build-loader-nav-button .material-icons {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  opacity: 0.7;
}
.build-loader-nav-button .material-icons.arrow {
  width: auto;
  height: auto;
  font-size: 20px;
}
.build-loader-ai-button {
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 4px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(90deg, #2563eb, #6d5dfc);
  color: white;
  padding: 0 16px;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
}
.build-loader-ai-button:disabled {
  cursor: default;
  opacity: 1;
}
.build-loader-ai-button .material-icons {
  font-size: 18px;
}
.build-loader-workspace {
  height: calc(100vh - 64px);
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}
.build-loader-canvas {
  position: relative;
  overflow: hidden;
  background: #f7f7f8;
}
.content-loader-phase {
  padding: 0;
  align-items: center;
  justify-content: center;
  gap: 0;
}
.content-loader-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: translateY(-34px);
}
.content-loader-sheet {
  width: min(374px, 46vw);
  min-width: 260px;
  height: 218px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.58;
  filter: drop-shadow(0 10px 18px rgba(15, 23, 42, 0.04));
}
.content-loader-sheet-inner {
  width: 100%;
  height: 100%;
  overflow: hidden;
  border: 1px solid rgba(218, 223, 231, 0.86);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}
.content-loader-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  height: 18px;
  border-bottom: 1px solid rgba(228, 232, 239, 0.86);
}
.content-loader-head {
  height: 18px;
}
.content-loader-cell {
  display: flex;
  align-items: center;
  padding: 0 11px;
  border-right: 1px solid rgba(228, 232, 239, 0.86);
  transition: background 320ms var(--ease);
}
.content-loader-cell:last-child {
  border-right: 0;
}
.content-loader-cell.active {
  background: rgba(48, 83, 255, 0.09);
}
.content-loader-bar {
  height: 4px;
  border-radius: 999px;
  background: #dfe4eb;
  opacity: 0.82;
}
.content-loader-bar.head {
  width: 50%;
  background: #d8dee8;
}
.content-loader-cell.active .content-loader-bar {
  height: 5px;
  background: #7fa5f7;
  opacity: 1;
}
.content-loader-cell.active .content-loader-bar.head {
  background: #3053ff;
}
.planning-shuffle-illustration {
  width: min(448px, 52vw);
  min-width: 300px;
  height: 336px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.58;
  filter: drop-shadow(0 10px 18px rgba(15, 23, 42, 0.04));
}
.planning-shuffle-grid {
  position: relative;
  width: 400px;
  height: 320px;
  transform-origin: center;
}
.planning-shuffle-card {
  position: absolute;
  border: 1px solid rgba(218, 223, 231, 0.88);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 2px rgba(10, 14, 26, 0.03);
  transition:
    left 700ms var(--ease),
    top 700ms var(--ease),
    width 700ms var(--ease),
    height 700ms var(--ease),
    box-shadow 400ms var(--ease);
  will-change: left, top, width, height;
}
.planning-shuffle-card::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    110deg,
    transparent 30%,
    rgba(48, 83, 255, 0.07) 50%,
    transparent 70%
  );
  background-size: 250% 100%;
  animation: contentLoaderShimmer 2.4s linear infinite;
  opacity: 0.68;
  pointer-events: none;
}
.planning-shuffle-card.lifted {
  z-index: 10;
  box-shadow:
    0 8px 16px rgba(10, 14, 26, 0.08),
    0 2px 4px rgba(10, 14, 26, 0.05);
}
.planning-shuffle-slot {
  position: absolute;
  border: 1.5px dashed rgba(214, 220, 230, 0.95);
  border-radius: 8px;
  background: transparent;
  opacity: 0.6;
  pointer-events: none;
  transition: opacity 500ms var(--ease), border-color 500ms var(--ease);
}
.planning-shuffle-slot.settled {
  border-color: rgba(48, 83, 255, 0.5);
  opacity: 0;
}
.content-loader-copy {
  margin-top: 18px;
  text-align: center;
}
.content-loader-copy h2 {
  margin: 0;
  color: #111215;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;
}
.content-loader-copy p {
  margin: 16px 0 0;
  color: #5f6368;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
}
.content-loader-progress {
  position: fixed;
  left: calc(50% - 180px);
  bottom: 10%;
  z-index: 80;
  width: min(506px, calc(100% - 48px));
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  padding: 12px 24px 13px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.14);
  transform: translateX(-50%);
  pointer-events: none;
}
.content-loader-progress-step {
  min-width: 0;
}
.content-loader-progress-track {
  height: 5px;
  overflow: hidden;
  border-radius: 999px;
  background: #d7d9df;
}
.content-loader-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: #3053ff;
  transition: width 220ms linear;
}
.content-loader-progress-step > div:last-child {
  margin-top: 11px;
  color: #a0a3aa;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}
.content-loader-progress-step > div:last-child.active {
  color: #3053ff;
}
.splash {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: grid;
  place-items: center;
  background: #f7f7f8;
  transition: opacity 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}
.splash-mark {
  width: 48px;
  height: 48px;
  position: relative;
  border-radius: 50%;
  background: conic-gradient(from 0deg, #3a5cff, #1db6b0, #3a5cff);
  animation: spin 2s linear infinite;
}
.splash-mark::after {
  content: "";
  position: absolute;
  inset: 7px;
  border-radius: 50%;
  background: #f7f7f8;
}
.build-loader-canvas-scrubber {
  position: absolute;
  left: 50%;
  bottom: 28px;
  z-index: 5;
  width: min(620px, calc(100% - 64px));
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.08);
  transform: translateX(-50%);
}
.canvas-scrub-icon {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #f3f4f6;
}
.canvas-scrub-icon span {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #2563eb;
  border-top-color: transparent;
  animation: spin 900ms linear infinite;
}
.canvas-scrub-body {
  flex: 1;
  min-width: 0;
}
.canvas-scrub-labels,
.canvas-scrub-bars {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 5px;
}
.canvas-scrub-labels {
  margin-bottom: 6px;
  color: #9ca3af;
  font-size: 11px;
  font-weight: 600;
}
.canvas-scrub-label.active {
  color: #111827;
}
.canvas-scrub-phase {
  height: 5px;
  overflow: hidden;
  border-radius: 99px;
  background: #e5e7eb;
}
.canvas-scrub-fill {
  height: 100%;
  border-radius: inherit;
  background: #2563eb;
  transition: width 220ms linear;
}
.phase {
  position: absolute;
  inset: 0;
  padding: 72px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.5s var(--ease);
}
.phase.active {
  opacity: 1;
  pointer-events: auto;
}
.narration {
  margin: 0;
  color: var(--ink);
  font-family: Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-feature-settings: "ss01";
}
.narration.minimal {
  display: flex;
  align-items: baseline;
  gap: 2px;
  color: var(--ink-2);
  font-size: clamp(36px, 4vw, 52px);
  font-weight: 500;
  letter-spacing: -0.025em;
  line-height: 1.05;
}
.narration.minimal .dots {
  display: inline-flex;
  margin-left: 2px;
}
.narration.minimal .dots span {
  opacity: 0.3;
  animation: dotFade 1.4s ease-in-out infinite;
}
.narration.minimal .dots span:nth-child(2) { animation-delay: 0.2s; }
.narration.minimal .dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes dotFade {
  0%, 100% { opacity: 0.25; }
  50% { opacity: 1; }
}
.skel-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
}
.skel-sheet {
  flex: 1;
  position: relative;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: white;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.skel-row {
  display: grid;
  grid-template-columns: 36px repeat(6, 1fr);
  min-height: 34px;
  border-bottom: 1px solid var(--line);
}
.skel-row:last-child { border-bottom: 0; }
.skel-row.skel-head {
  min-height: 36px;
  background: var(--bg-2);
}
.skel-cell {
  display: flex;
  align-items: center;
  padding: 0 14px;
  border-right: 1px solid var(--line);
  transition: background 0.4s var(--ease);
}
.skel-cell:last-child { border-right: 0; }
.skel-cell.skel-gutter {
  padding: 0;
  background: var(--bg-2);
}
.skel-cell.active { background: rgba(58, 92, 255, 0.06); }
.skel-bar {
  height: 8px;
  border-radius: 3px;
  background: #eaeef3;
  transition: background 0.4s var(--ease);
}
.skel-cell.active .skel-bar { background: #b7c2ff; }
.skel-bar-head {
  height: 6px;
  background: #d6dce6;
}
.skel-cell.active .skel-bar-head { background: var(--accent); }
.skel-scanline {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, transparent, rgba(58, 92, 255, 0.6), transparent);
  box-shadow: 0 0 16px 2px rgba(58, 92, 255, 0.3);
  transition: left 0.6s var(--ease);
  pointer-events: none;
}
.dashboard-skeleton-phase {
  padding: 28px 26px;
  gap: 0;
  overflow: hidden;
}
.dashboard-plan-skeleton {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  opacity: 0.42;
}
.skeleton-dashboard-header {
  flex-shrink: 0;
  margin-bottom: 22px;
}
.skeleton-dashboard-header h1 {
  margin: 0 0 6px;
  color: #111827;
  font-size: 20px;
  font-weight: 650;
  letter-spacing: -0.02em;
}
.skeleton-dashboard-header h1 .dots {
  display: inline-flex;
  margin-left: 3px;
  color: #2563eb;
}
.skeleton-dashboard-header h1 .dots span {
  opacity: 0.3;
  animation: dotFade 1.4s ease-in-out infinite;
}
.skeleton-dashboard-header h1 .dots span:nth-child(2) {
  animation-delay: 0.2s;
}
.skeleton-dashboard-header h1 .dots span:nth-child(3) {
  animation-delay: 0.4s;
}
.skeleton-dashboard-header p {
  margin: 0;
  max-width: 960px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.45;
}
.skeleton-filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-bottom: 18px;
  color: #9ca3af;
  font-size: 12px;
}
.skeleton-filter-row .material-icons {
  font-size: 15px;
  opacity: 0.75;
}
.skeleton-filter-pill {
  height: 22px;
  display: inline-flex;
  align-items: center;
  border-radius: 6px;
  background: #f2f4f7;
  color: #a3aab7;
  padding: 0 11px;
}
.skeleton-filter-pill.wide {
  width: 110px;
  color: transparent;
}
.dashboard-skeleton-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-template-rows: 150px repeat(2, minmax(0, 1fr));
  gap: 18px;
}
.dashboard-skeleton-card {
  position: relative;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.02);
}
.dashboard-skeleton-card::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(110deg, transparent 20%, rgba(58, 92, 255, 0.03) 45%, rgba(58, 92, 255, 0.08) 50%, rgba(58, 92, 255, 0.03) 55%, transparent 80%);
  background-size: 300% 100%;
  animation: shimmer 2.2s linear infinite;
}
.dashboard-skeleton-card.metric {
  min-height: 112px;
}
.dashboard-skeleton-card.metric span {
  position: absolute;
  top: 18px;
  left: 18px;
  width: 42%;
  height: 9px;
  border-radius: 999px;
  background: #eef2f7;
}
.dashboard-skeleton-card.large {
  grid-column: span 2;
  min-height: 210px;
}
.dashboard-build-skeleton {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  opacity: calc(0.48 + (var(--build-progress, 0) * 0.16));
}
.dashboard-build-skeleton-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-template-rows: 144px repeat(2, minmax(0, 1fr));
  gap: 18px;
}
.dashboard-build-skeleton-card {
  position: relative;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.025);
}
.dashboard-build-skeleton-card::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(110deg, transparent 20%, rgba(58, 92, 255, 0.025) 45%, rgba(58, 92, 255, 0.075) 50%, rgba(58, 92, 255, 0.025) 55%, transparent 80%);
  background-size: 300% 100%;
  animation: shimmer 2s linear infinite;
  pointer-events: none;
}
.dashboard-build-skeleton-card.metric {
  padding: 18px;
}
.skeleton-card-title {
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;
}
.skeleton-card-title.loading {
  color: #6b7280;
}
.skeleton-card-dot {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e5e7eb;
}
.skeleton-metric-value {
  margin-top: 26px;
  color: #9ca3af;
  font-size: 24px;
  font-weight: 650;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.03em;
  transition: opacity 180ms var(--ease);
}
.skeleton-subvalue-bar {
  width: 38%;
  height: 8px;
  margin-top: 28px;
  border-radius: 2px;
  background: #e9edf2;
}
.dashboard-build-skeleton-card.chart {
  grid-column: span 2;
  min-height: 220px;
  padding: 22px;
}
.line-chart svg {
  width: 100%;
  height: calc(100% - 28px);
  margin-top: 24px;
  overflow: visible;
}
.line-chart svg path:first-child {
  stroke: #cfd4dc;
  stroke-width: 3;
  stroke-linejoin: round;
  stroke-linecap: round;
}
.line-chart svg path:last-child {
  fill: #eef0f3;
}
.skeleton-bars {
  height: calc(100% - 48px);
  margin-top: 28px;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  align-items: end;
  gap: 14px;
}
.skeleton-bars span {
  display: block;
  min-height: 36px;
  border-radius: 3px 3px 0 0;
  background: #e5e7eb;
}
.skeleton-horizontal-bars {
  margin-top: 34px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.skeleton-horizontal-bars span {
  display: block;
  height: 14px;
  border-radius: 2px;
  background: #e5e7eb;
}
.skeleton-donut {
  width: min(176px, 48%);
  aspect-ratio: 1;
  margin: 38px auto 0;
  border-radius: 50%;
  background: conic-gradient(#e5e7eb 0 25%, #eef0f3 25% 56%, #e5e7eb 56% 78%, #f1f3f6 78% 100%);
  position: relative;
}
.skeleton-donut::after {
  content: "";
  position: absolute;
  inset: 28%;
  border-radius: 50%;
  background: white;
}
@keyframes shimmer {
  from { background-position: 150% 0; }
  to { background-position: -150% 0; }
}
@keyframes contentLoaderShimmer {
  from { background-position: 200% 0; }
  to { background-position: -100% 0; }
}
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 1100px) {
  .build-loader-nav-label { display: none; }
}
@media (max-width: 900px) {
  .build-loader-navbar { padding: 12px 16px; }
  .build-loader-nav-title { display: none; }
  .build-loader-workspace { grid-template-columns: 1fr; }
  .phase { padding: 36px 24px; }
  .planning-shuffle-grid { transform: scale(0.82); }
  .dashboard-skeleton-phase { padding: 24px 20px; }
  .dashboard-skeleton-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(2, 120px) repeat(2, minmax(180px, 1fr));
  }
  .dashboard-build-skeleton-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(2, 120px) repeat(2, minmax(180px, 1fr));
  }
  .dashboard-skeleton-card.large,
  .dashboard-build-skeleton-card.chart { grid-column: span 2; }
}
`;
